import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { format, startOfDay, subDays, addDays, subWeeks, addWeeks, subMonths, addMonths, subYears, addYears, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, isSameDay, isWithinInterval } from 'date-fns';

/* ── Relative date presets ── */
const DIRECTION_OPTIONS = [
    { value: 'this', label: 'This' },
    { value: 'past', label: 'Past' },
    { value: 'next', label: 'Next' },
];

const UNIT_OPTIONS = [
    { value: 'day', label: 'day' },
    { value: 'week', label: 'week' },
    { value: 'month', label: 'month' },
    { value: 'year', label: 'year' },
];

const DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Resolve a relative config { direction, amount, unit } into a { start, end } Date range */
function resolveRelativeRange(direction, amount, unit) {
    const now = new Date();
    const today = startOfDay(now);
    const n = parseInt(amount, 10) || 1;

    if (direction === 'this') {
        switch (unit) {
            case 'day': return { start: today, end: today };
            case 'week': return { start: startOfWeek(today), end: endOfWeek(today) };
            case 'month': return { start: startOfMonth(today), end: endOfMonth(today) };
            case 'year': return { start: startOfYear(today), end: endOfYear(today) };
            default: return { start: today, end: today };
        }
    }
    if (direction === 'past') {
        switch (unit) {
            case 'day': return { start: subDays(today, n), end: today };
            case 'week': return { start: subWeeks(today, n), end: today };
            case 'month': return { start: subMonths(today, n), end: today };
            case 'year': return { start: subYears(today, n), end: today };
            default: return { start: today, end: today };
        }
    }
    if (direction === 'next') {
        switch (unit) {
            case 'day': return { start: today, end: addDays(today, n) };
            case 'week': return { start: today, end: addWeeks(today, n) };
            case 'month': return { start: today, end: addMonths(today, n) };
            case 'year': return { start: today, end: addYears(today, n) };
            default: return { start: today, end: today };
        }
    }
    return { start: today, end: today };
}

/** Parse a value (string or Date) into a safe Date */
function parseSafe(d) {
    if (!d) return null;
    if (d instanceof Date) return isNaN(d.getTime()) ? null : d;
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
        const [y, m, day] = d.split('-').map(Number);
        return new Date(y, m - 1, day);
    }
    const parsed = new Date(d);
    return isNaN(parsed.getTime()) ? null : parsed;
}


/* ── Mini Calendar Component ── */
function MiniCalendar({ year, month, onNav, selectedStart, selectedEnd, onSelect, highlightRange }) {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = startOfDay(new Date());

    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    // Pad end to complete the last row
    while (cells.length % 7 !== 0) cells.push(null);

    const isInRange = (date) => {
        if (!highlightRange?.start || !highlightRange?.end || !date) return false;
        const s = highlightRange.start < highlightRange.end ? highlightRange.start : highlightRange.end;
        const e = highlightRange.start < highlightRange.end ? highlightRange.end : highlightRange.start;
        try {
            return isWithinInterval(date, { start: startOfDay(s), end: startOfDay(e) });
        } catch { return false; }
    };

    const isRangeStart = (date) => {
        if (!highlightRange?.start || !date) return false;
        const s = highlightRange.start < highlightRange.end ? highlightRange.start : highlightRange.end;
        return isSameDay(date, s);
    };

    const isRangeEnd = (date) => {
        if (!highlightRange?.end || !date) return false;
        const e = highlightRange.start < highlightRange.end ? highlightRange.end : highlightRange.start;
        return isSameDay(date, e);
    };

    const isSelectedDate = (date) => {
        if (selectedStart && date && isSameDay(date, selectedStart)) return true;
        if (selectedEnd && date && isSameDay(date, selectedEnd)) return true;
        return false;
    };
    const isTodayDate = (date) => date && isSameDay(date, today);

    // Cell size
    const CELL = 34;
    const GAP = 2;

    return (
        <div className="p-[10px_12px_10px]">
            {/* ── Month/Year navigation header ── */}
            <div className="flex items-center justify-between mb-2 px-0.5">
                <button type="button"
                    onClick={(e) => { e.stopPropagation(); onNav(-1); }}
                    className="w-7 h-7 bg-transparent border border-transparent cursor-pointer rounded-md text-[var(--text-secondary)] flex items-center justify-center transition-all duration-150 hover:bg-[var(--bg-hover)] hover:border-[var(--border)]"
                >
                    <ObliqueIcon name="chevron_left" size={18} />
                </button>
                <span className="font-semibold text-[13px] text-[var(--text-primary)] tracking-tight select-none">
                    {MONTH_NAMES[month]} {year}
                </span>
                <button type="button"
                    onClick={(e) => { e.stopPropagation(); onNav(1); }}
                    className="w-7 h-7 bg-transparent border border-transparent cursor-pointer rounded-md text-[var(--text-secondary)] flex items-center justify-center transition-all duration-150 hover:bg-[var(--bg-hover)] hover:border-[var(--border)]"
                >
                    <ObliqueIcon name="chevron_right" size={18} />
                </button>
            </div>

            {/* ── Day-of-week headers ── */}
            <div className="grid grid-cols-[repeat(7,34px)] gap-x-[2px] justify-center mb-1 border-b border-[var(--border-subtle)] pb-1.5">
                {DAYS_OF_WEEK.map(d => (
                    <div key={d} className="text-center text-[11px] font-medium text-[var(--text-tertiary)] h-5 leading-5 select-none uppercase tracking-wide">
                        {d}
                    </div>
                ))}
            </div>

            {/* ── Day cells grid ── */}
            <div className="grid grid-cols-[repeat(7,34px)] gap-[2px] justify-center">
                {cells.map((date, i) => {
                    if (!date) return <div key={`empty-${i}`} className="w-[34px] h-[34px]" />;

                    const day = date.getDate();
                    const selected = isSelectedDate(date);
                    const inRange = isInRange(date);
                    const rangeStart = isRangeStart(date);
                    const rangeEnd = isRangeEnd(date);
                    const todayMark = isTodayDate(date);

                    // Build the cell background and border-radius for connected range effect
                    let cellBg = 'bg-transparent';
                    let cellColor = 'text-[var(--text-primary)]';
                    let cellWeight = 'font-normal';
                    let cellRadius = 'rounded-lg';
                    let cellBorder = 'border-2 border-transparent';

                    if (selected) {
                        cellBg = 'bg-[var(--accent)]';
                        cellColor = 'text-white';
                        cellWeight = 'font-semibold';
                        cellRadius = 'rounded-lg';
                    } else if (inRange && !rangeStart && !rangeEnd) {
                        cellBg = 'bg-blue-600/10'; // rgba(37, 99, 235, 0.07) is roughly bg-blue-600/10
                        cellColor = 'text-[var(--accent)]';
                        cellWeight = 'font-medium';
                        // Connected strip — no radius on middle cells
                        const colIdx = i % 7;
                        if (colIdx === 0) cellRadius = 'rounded-l-lg rounded-r-none'; // row start
                        else if (colIdx === 6) cellRadius = 'rounded-r-lg rounded-l-none'; // row end
                        else cellRadius = 'rounded-none';
                    } else if (rangeStart && !selected) {
                        cellBg = 'bg-blue-600/10';
                        cellColor = 'text-[var(--accent)]';
                        cellWeight = 'font-semibold';
                        cellRadius = 'rounded-l-lg rounded-r-none';
                    } else if (rangeEnd && !selected) {
                        cellBg = 'bg-blue-600/10';
                        cellColor = 'text-[var(--accent)]';
                        cellWeight = 'font-semibold';
                        cellRadius = 'rounded-r-lg rounded-l-none';
                    } else if (todayMark) {
                        cellColor = 'text-[var(--accent)]';
                        cellWeight = 'font-semibold';
                        cellBorder = 'border-2 border-[var(--accent)]';
                    }

                    // For the unselected base hover state we can just append hover utilities to the className

                    return (
                        <div
                            key={day}
                            onClick={(e) => { e.stopPropagation(); onSelect(date); }}
                            className={`w-[34px] h-[34px] flex items-center justify-center text-[12.5px] cursor-pointer select-none relative box-border transition-all duration-100 ease-in ${cellWeight} ${cellRadius} ${cellColor} ${cellBg} ${cellBorder} ${!selected && !inRange && !todayMark ? 'hover:bg-[var(--bg-hover)]' : ''}`}
                        >
                            {day}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}



/* ── Small Dropdown for relative date selectors ── */
function SmallSelect({ value, options, onChange, style: extraStyle = {} }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const selectedLabel = options.find(o => o.value === value)?.label || value;

    return (
        <div ref={ref} style={{ ...extraStyle }} className="relative">
            <div onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
                className="h-7 px-[8px] pr-[22px] rounded-md border border-[var(--border)] bg-[var(--bg-subtle)] text-[13px] font-medium text-[var(--text-primary)] cursor-pointer flex items-center whitespace-nowrap relative select-none transition-colors duration-150 hover:bg-[var(--bg-muted)]"
            >
                {selectedLabel}
                <ObliqueIcon name="keyboard_arrow_down" size={14} color="var(--text-tertiary)"
                    style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)' }}
                />
            </div>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 3 }}
                        transition={{ duration: 0.1 }}
                        className="absolute top-[calc(100%+3px)] left-0 min-w-full bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg shadow-md z-[6000] p-[3px] overflow-hidden"
                    >
                        {options.map(opt => (
                            <div key={opt.value}
                                onClick={(e) => { e.stopPropagation(); onChange(opt.value); setOpen(false); }}
                                className={`p-[5px_10px] text-[13px] rounded-[5px] cursor-pointer text-[var(--text-primary)] flex items-center justify-between gap-2 hover:bg-[var(--bg-muted)] ${opt.value === value ? 'bg-[var(--bg-muted)]' : 'bg-transparent'}`}
                            >
                                <span>{opt.label}</span>
                                {opt.value === value && <ObliqueIcon name="check" size={14} color="var(--accent)" />}
                            </div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}


/* ══════════════════════════════════════════════
   Main Component: GridDateValuePicker
   ══════════════════════════════════════════════ */
const GridDateValuePicker = ({ operator, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    const isRelative = operator === 'date_is_within';
    const isBetween = operator === 'date_is_between';

    // Calendar navigation state
    const now = new Date();
    const [calMonth, setCalMonth] = useState(now.getMonth());
    const [calYear, setCalYear] = useState(now.getFullYear());

    // Relative date state
    const [direction, setDirection] = useState(
        (typeof value === 'object' && value?.direction) || 'this'
    );
    const [amount, setAmount] = useState(
        (typeof value === 'object' && value?.amount) || 1
    );
    const [unit, setUnit] = useState(
        (typeof value === 'object' && value?.unit) || 'week'
    );

    // Between mode: track which date we're picking (start or end)
    const [betweenPhase, setBetweenPhase] = useState('start');

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    // When opening, set calendar to show the relevant month
    useEffect(() => {
        if (isOpen) {
            let focusDate = null;
            if (isRelative) {
                focusDate = new Date();
            } else if (value) {
                focusDate = parseSafe(value?.start || value);
            }
            if (focusDate && !isNaN(focusDate.getTime())) {
                setCalMonth(focusDate.getMonth());
                setCalYear(focusDate.getFullYear());
            } else {
                setCalMonth(now.getMonth());
                setCalYear(now.getFullYear());
            }
        }
    }, [isOpen]);

    // Compute the highlight range for relative mode
    const relativeRange = useMemo(() => {
        if (!isRelative) return null;
        return resolveRelativeRange(direction, amount, unit);
    }, [isRelative, direction, amount, unit]);

    // Emit relative changes
    const emitRelative = (dir, amt, u) => {
        const range = resolveRelativeRange(dir, amt, u);
        onChange({
            direction: dir, amount: amt, unit: u,
            start: range.start, end: range.end,
            preset: dir === 'this' ? `this_${u}` : `${dir}_${amt}_${u}`
        });
    };

    // Calendar nav
    const handleNav = (dir) => {
        let m = calMonth + dir, y = calYear;
        if (m < 0) { m = 11; y--; } else if (m > 11) { m = 0; y++; }
        setCalMonth(m); setCalYear(y);
    };

    // Handle single date selection
    const handleDateSelect = (date) => {
        if (isBetween) {
            if (betweenPhase === 'start') {
                const existing = value && typeof value === 'object' ? value : {};
                onChange({ ...existing, start: date, end: existing.end || date });
                setBetweenPhase('end');
            } else {
                const existing = value && typeof value === 'object' ? value : {};
                let s = parseSafe(existing.start) || date;
                let e = date;
                if (e < s) { [s, e] = [e, s]; }
                onChange({ start: s, end: e });
                setBetweenPhase('start');
                setIsOpen(false);
            }
        } else {
            onChange({ start: date, end: date });
            setIsOpen(false);
        }
    };

    // Format the display label
    let selectedLabel = 'Select date...';
    if (isRelative && value) {
        const dir = value.direction || direction;
        const amt = value.amount || amount;
        const u = value.unit || unit;
        if (dir === 'this') {
            selectedLabel = `This ${u}`;
        } else {
            selectedLabel = `${dir === 'past' ? 'Past' : 'Next'} ${amt} ${u}${amt > 1 ? 's' : ''}`;
        }
    } else if (isBetween && value?.start && value?.end) {
        const s = parseSafe(value.start);
        const e = parseSafe(value.end);
        if (s && e) {
            selectedLabel = `${format(s, 'MMM d, yyyy')} → ${format(e, 'MMM d, yyyy')}`;
        }
    } else if (value) {
        const d = parseSafe(value?.start || value);
        if (d) selectedLabel = format(d, 'MMM d, yyyy');
    }

    // Compute highlight range for calendar
    let calHighlightRange = null;
    if (isRelative && relativeRange) {
        calHighlightRange = relativeRange;
    } else if (isBetween && value?.start && value?.end) {
        calHighlightRange = { start: parseSafe(value.start), end: parseSafe(value.end) };
    }

    return (
        <div ref={containerRef} className="relative w-full flex-1">
            {/* Trigger */}
            <div
                onClick={() => setIsOpen(!isOpen)}
                className={`h-7 rounded-md border bg-[var(--bg-subtle)] text-[13px] px-2 font-normal cursor-pointer outline-none flex items-center justify-between w-full transition-all duration-150 hover:bg-[var(--bg-muted)] hover:border-[var(--border-strong)] ${isOpen ? 'border-[var(--border-strong)]' : 'border-[var(--border)]'} ${value ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'}`}
            >
                <div className="flex items-center gap-1.5 overflow-hidden">
                    <ObliqueIcon name="calendar_today" size={14} color="var(--text-tertiary)" />
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                        {selectedLabel}
                    </span>
                </div>
                <ObliqueIcon name="keyboard_arrow_down" size={16} color="var(--text-tertiary)" />
            </div>

            {/* Popover */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 4, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.98 }}
                        transition={{ duration: 0.12 }}
                        className="absolute top-[calc(100%+4px)] left-0 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-lg z-[5000] overflow-hidden w-[280px]"
                    >
                        {isRelative ? (
                            /* ── Relative Date Mode ── */
                            <div>
                                {/* Direction + Amount + Unit dropdowns */}
                                <div className="p-[10px_12px_6px] flex items-center gap-1.5">
                                    <SmallSelect
                                        value={direction}
                                        options={DIRECTION_OPTIONS}
                                        onChange={(v) => { setDirection(v); emitRelative(v, amount, unit); }}
                                        style={{ flex: '0 0 auto' }}
                                    />
                                    {direction !== 'this' && (
                                        <input
                                            type="number"
                                            min={1}
                                            value={amount}
                                            onChange={(e) => {
                                                const v = Math.max(1, parseInt(e.target.value, 10) || 1);
                                                setAmount(v);
                                                emitRelative(direction, v, unit);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-12 h-7 rounded-md border border-[var(--border)] bg-[var(--bg-subtle)] text-[13px] font-medium text-center text-[var(--text-primary)] outline-none px-1 shadow-none"
                                        />
                                    )}
                                    <SmallSelect
                                        value={unit}
                                        options={UNIT_OPTIONS}
                                        onChange={(v) => { setUnit(v); emitRelative(direction, amount, v); }}
                                        style={{ flex: '0 0 auto' }}
                                    />
                                </div>

                                {/* Preview calendar showing the highlighted relative range */}
                                <MiniCalendar
                                    year={calYear}
                                    month={calMonth}
                                    onNav={handleNav}
                                    selectedStart={relativeRange?.start}
                                    selectedEnd={relativeRange?.end}
                                    onSelect={() => {}}
                                    highlightRange={relativeRange}
                                />

                                {/* Info footer */}
                                <div className="p-[8px_14px_10px] text-[11px] text-[var(--text-tertiary)] border-t border-[var(--border-subtle)] flex items-center gap-[5px]">
                                    <ObliqueIcon name="info" size={13} color="var(--text-tertiary)" />
                                    Rolling filter — updates with today's date
                                </div>
                            </div>
                        ) : isBetween ? (
                            /* ── Between Mode: pick start and end ── */
                            <div>
                                {/* Phase tabs */}
                                <div className="flex border-b border-[var(--border-subtle)] mx-3">
                                    {['start', 'end'].map(phase => {
                                        const isActive = betweenPhase === phase;
                                        const dateVal = phase === 'start' ? value?.start : value?.end;
                                        const label = dateVal ? format(parseSafe(dateVal) || new Date(), 'MMM d, yyyy') : '\u2014';
                                        return (
                                            <div
                                                key={phase}
                                                onClick={(e) => { e.stopPropagation(); setBetweenPhase(phase); }}
                                                className={`flex-1 pt-[10px] pb-2 cursor-pointer text-center text-xs transition-all duration-150 select-none border-b-2 ${isActive ? 'font-semibold text-[var(--accent)] border-[var(--accent)]' : 'font-normal text-[var(--text-tertiary)] border-transparent hover:text-[var(--text-primary)]'}`}
                                            >
                                                <div className="text-[10px] uppercase tracking-[0.05em] mb-[2px] opacity-70">
                                                    {phase === 'start' ? 'From' : 'To'}
                                                </div>
                                                {label}
                                            </div>
                                        );
                                    })}
                                </div>

                                <MiniCalendar
                                    year={calYear}
                                    month={calMonth}
                                    onNav={handleNav}
                                    selectedStart={parseSafe(value?.start)}
                                    selectedEnd={parseSafe(value?.end)}
                                    onSelect={handleDateSelect}
                                    highlightRange={calHighlightRange}
                                />
                            </div>
                        ) : (
                            /* ── Specific Date Mode (date_is, date_is_before, etc.) ── */
                            <MiniCalendar
                                year={calYear}
                                month={calMonth}
                                onNav={handleNav}
                                selectedStart={parseSafe(value?.start || value)}
                                selectedEnd={null}
                                onSelect={handleDateSelect}
                                highlightRange={null}
                            />
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default GridDateValuePicker;
